
function syncDebugEnabled() {
  if (typeof window === "undefined") return true;
  if (window.__SYNC_DEBUG__ === false) return false;
  try {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem("SYNC_DEBUG") === "0") return false;
  } catch {
    // storage blocked (privacy mode) — keep debug logging on
  }
  return true;
}
function logSync(kind, cacheKey, outcome) {
  if (!syncDebugEnabled()) return;
  let keyLabel;
  try {
    keyLabel = JSON.stringify(cacheKey);
  } catch {
    keyLabel = String(cacheKey);
  }
  console.debug(`[sync:${kind}]`, keyLabel, outcome);
}
function finish(kind, cacheKey, result) {
  const { snapshot: _snapshot, items: _items, ...loggable } = result;
  logSync(kind, cacheKey, loggable);
  return result;
}

const engineState = new Map();

function stateKeyFor(cacheKey) {
  return JSON.stringify(cacheKey);
}

function getState(cacheKey) {
  const key = stateKeyFor(cacheKey);
  let state = engineState.get(key);
  if (!state) {
    state = { appliedIds: new Set(), groupTimestamps: {}, pendingPartials: [] };
    engineState.set(key, state);
  }
  return state;
}

export function resetChannelSyncState(cacheKey) {
  engineState.delete(stateKeyFor(cacheKey));
}

function getPath(obj, path) {
  let cur = obj;
  for (const part of path.split(".")) {
    if (cur == null) return undefined;
    cur = cur[part];
  }
  return cur;
}

function setPath(obj, path, value) {
  const parts = path.split(".");
  const out = { ...obj };
  let cur = out;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    cur[part] = { ...(cur[part] || {}) };
    cur = cur[part];
  }
  cur[parts[parts.length - 1]] = value;
  return out;
}

function rollbackIneligibleGroups(candidate, current, ineligibleGroups, config) {
  if (!current || !ineligibleGroups.length) return candidate;
  let out = candidate;
  for (const group of ineligibleGroups) {
    for (const path of config.fieldsForGroup(group)) {
      const candidateVal = getPath(out, path);
      const currentVal = getPath(current, path);
      // Only restore a value we actually have: writing `undefined` here deletes the key
      // from the candidate (that is how streamToUser vanished from the admin cache).
      if (currentVal === undefined) continue;
      if (candidateVal !== currentVal) {
        out = setPath(out, path, currentVal);
      }
    }
  }
  return out;
}

function applyOne({ queryClient, cacheKey, message, config, state }) {
  const { id, raw } = message;
  const isRest = !!message.isRest;
  const done = (result) => finish("channel", cacheKey, { id, isRest, ...result });

  if (id != null && state.appliedIds.has(id)) {
    return done({ applied: false, reason: "duplicate" });
  }

  const { kind, groups } = config.classify(raw) || { kind: "ignore", groups: [] };
  if (kind === "ignore" || !groups.length) {
    if (id != null) state.appliedIds.add(id);
    return done({ applied: false, reason: "unrecognized" });
  }

  const effectiveTimestamp = isRest ? 0 : typeof message.timestamp === "number" ? message.timestamp : Date.now();

  const eligibleGroups = groups.filter((group) => {
    const last = state.groupTimestamps[group];
    return last == null || effectiveTimestamp >= last;
  });

  if (!eligibleGroups.length) {
    if (id != null) state.appliedIds.add(id);
    return done({ applied: false, reason: "stale", groups, timestamp: effectiveTimestamp });
  }

  const current = queryClient.getQueryData(cacheKey) ?? null;

  if (kind === "partial" && !current) {
    // Queue (not a single slot): every partial that arrives before the base
    // must survive, otherwise earlier groups are silently dropped on flush.
    state.pendingPartials.push(message);
    return done({ applied: false, reason: "buffered-pending-base" });
  }

  const candidate = config.computeCandidate({ raw, current, kind });
  if (!candidate) {
    if (id != null) state.appliedIds.add(id);
    return done({ applied: false, reason: "no-op" });
  }

  const ineligibleGroups = groups.filter((group) => !eligibleGroups.includes(group));
  const finalSnapshot = rollbackIneligibleGroups(candidate, current, ineligibleGroups, config);

  queryClient.setQueryData(cacheKey, finalSnapshot);
  for (const group of eligibleGroups) state.groupTimestamps[group] = effectiveTimestamp;
  if (id != null) state.appliedIds.add(id);

  return done({ applied: true, groups: eligibleGroups, snapshot: finalSnapshot, timestamp: effectiveTimestamp });
}

export function applyChannelMessage({ queryClient, cacheKey, message, config }) {
  const state = getState(cacheKey);
  const result = applyOne({ queryClient, cacheKey, message, config, state });

  if (result.applied && state.pendingPartials.length) {
    const pendings = state.pendingPartials.splice(0);
    for (const pending of pendings) {
      applyOne({ queryClient, cacheKey, message: pending, config, state });
    }
  }

  return result;
}


const keyedListState = new Map();

function getKeyedListState(cacheKey) {
  const key = stateKeyFor(cacheKey);
  let state = keyedListState.get(key);
  if (!state) {
    state = { appliedIds: new Set(), itemTimestamps: new Map() };
    keyedListState.set(key, state);
  }
  return state;
}

export function resetKeyedListState(cacheKey) {
  keyedListState.delete(stateKeyFor(cacheKey));
}

export function upsertMerge(current, eligibleItems, getId) {
  const list = Array.isArray(current) ? current : [];
  const byId = new Map(list.map((item) => [getId(item), item]));
  for (const item of eligibleItems) byId.set(getId(item), item);
  return [...byId.values()];
}

export function applyKeyedListMessage({ cacheKey, message, config }) {
  const state = getKeyedListState(cacheKey);
  const { id, raw } = message;
  const isRest = !!message.isRest;
  const done = (result) => finish("keyedList", cacheKey, { id, isRest, ...result });

  if (id != null && state.appliedIds.has(id)) {
    return done({ applied: false, reason: "duplicate" });
  }

  const { kind, items } = config.classify(raw) || { kind: "ignore", items: [] };
  if (kind === "ignore" || !items || !items.length) {
    if (id != null) state.appliedIds.add(id);
    return done({ applied: false, reason: "unrecognized" });
  }

  const effectiveTimestamp = isRest ? 0 : typeof message.timestamp === "number" ? message.timestamp : Date.now();

  const eligibleItems = [];
  const skippedIds = [];
  for (const item of items) {
    const itemId = config.getId(item);
    if (itemId == null) continue;
    const last = state.itemTimestamps.get(itemId);
    if (last == null || effectiveTimestamp >= last) eligibleItems.push(item);
    else skippedIds.push({ itemId, last });
  }

  if (id != null) state.appliedIds.add(id);

  if (!eligibleItems.length) return done({ applied: false, reason: "stale", timestamp: effectiveTimestamp, skippedIds });

  const current = config.getCurrent();
  const merge = config.merge || upsertMerge;
  const next = merge(current, eligibleItems, config.getId);
  config.setResult(next);

  for (const item of eligibleItems) {
    state.itemTimestamps.set(config.getId(item), effectiveTimestamp);
  }

  return done({ applied: true, items: eligibleItems, timestamp: effectiveTimestamp, resultLength: next?.length, skippedIds });
}
